import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import { useModal } from '../../hooks/useModal';
import { Modal } from '../../components/organisms/Modal/Modal';
import styles from './Inquiry.module.css';

const PROCESS_STEPS = [
  { n: '01', label: '신청서 제출', sub: '이메일 한 줄이면 시작' },
  { n: '02', label: '담당 변호사 배정', sub: '1영업일 이내' },
  { n: '03', label: '계약 조건 안내 및 체결' },
  { n: '04', label: '즉시 서비스 이용 시작' },
];

const PROGRESS_TICK_MS = 1400;

/**
 * Inquiry section — simplified email CTA + progress bar + shared modal flow.
 * Used both on landing (inline=false) and on detail pages (inline=true).
 * `inline` only switches the section id (#inquiry vs #page-inquiry).
 * `onSubmit` is optional — useful for analytics hooks at the consumer level.
 */
export function Inquiry({ onSubmit, inline = false, serviceContext = null }) {
  const { ref, inView } = useInView();
  const [activeStep, setActiveStep] = useState(0);
  const modal = useModal();

  // 무한 루프로 단계 진행 표시
  useEffect(() => {
    const t = setInterval(() => {
      setActiveStep((s) => (s + 1) % PROCESS_STEPS.length);
    }, PROGRESS_TICK_MS);
    return () => clearInterval(t);
  }, []);

  const fillPercent = ((activeStep + 1) / PROCESS_STEPS.length) * 100;
  const sectionId = inline ? 'page-inquiry' : 'inquiry';

  const handleOpenModal = () => {
    const payload = serviceContext ? { context: serviceContext } : {};
    modal.open(payload);
    onSubmit?.(payload);
  };

  return (
    <>
      <section className={styles.ctaSection} id={sectionId}>
        <div className="wrap">
          <div ref={ref} className={`${styles.ctaWrap} reveal ${inView ? 'visible' : ''}`}>
            {/* Progress bar — auto-cycling */}
            <div className={styles.progress} style={{ '--progress-fill': `${fillPercent}%` }}>
              {PROCESS_STEPS.map((step, i) => {
                const isActive = i === activeStep;
                const isDone = i < activeStep;
                return (
                  <div
                    key={i}
                    className={`${styles.progressStep} ${
                      isActive ? styles.progressStepActive : ''
                    } ${isDone ? styles.progressStepDone : ''}`}
                  >
                    <div className={styles.progressBadge}>
                      <span className={styles.progressNum}>{step.n}</span>
                    </div>
                    <div className={styles.progressText}>
                      <span className={styles.progressLabel}>{step.label}</span>
                      {step.sub && <span className={styles.progressSub}>{step.sub}</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            <h2 className={styles.ctaTitle}>
              지금 바로 신청하세요.
              <br />
              <span className={styles.ctaTitleAccent}>변호사가 직접 회신합니다.</span>
            </h2>
            <p className={styles.ctaSub}>
              신청서 작성 5분이면 충분합니다. 담당 변호사가 1영업일 이내에 연락드립니다.
            </p>

            <div className={styles.ctaBtnRow}>
              <button type="button" className={styles.primaryBtn} onClick={handleOpenModal}>
                상담 신청하기
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M3 8h10M9 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <p className={styles.ctaNotice}>
              제출하신 정보는 상담 목적으로만 활용되며, 외부에 제공되지 않습니다.
            </p>

            <p className={styles.groupLinkRow}>
              단체·기관 법률 자문 계약 문의는{' '}
              <Link to="/group-inquiry" className={styles.groupLinkA}>
                단체계약 안내 페이지 →
              </Link>
            </p>
          </div>
        </div>
      </section>

      <Modal isOpen={modal.isOpen} onClose={modal.close} email={modal.data?.email} />
    </>
  );
}
